import { selectedBusinessWorkspace } from '../../../../shared/platform/business-workspaces';
import { apiBaseOrigin } from '../../../../shared/config/api-origin';
import { workspaceFetch } from '../../../../shared/platform/business-workspaces';
export type MonitoringAuth = { session: string; csrf: string };
export type AssetRef = {
  assetId: string;
  country: string;
  agency: string;
  publishedVersion: number;
  digest: string;
  displayName: string;
  category: string;
};
export type Metric = { state: 'MEASURED' | 'UNKNOWN' | 'NOT_APPLICABLE'; value: number | null };
export type Observation = {
  sourceType: 'CONTROLLED_EXPERIMENT';
  sourceId: string;
  sourceLabel: string;
  observedAt: string;
  receivedAt: string;
  expiresAt: string;
  operation: string;
  connection: string;
  battery: Metric;
  temperature: Metric;
  runtimeHours: Metric;
  anomalies: string;
  notes: string;
};
export type Maintenance = {
  kind: 'PLAN' | 'CHECK' | 'FAULT';
  title: string;
  status: string;
  scheduledAt: string;
  occurredAt: string;
  details: string;
  resolution: string;
};
export type MonitoringEvent<T = Observation | Maintenance> = {
  eventId: string;
  recordId: string;
  revision: number;
  kind: string;
  asset: AssetRef;
  data: T;
  recordedAt: string;
  account: string;
  person: string;
  digest: string;
  sourceType: 'CONTROLLED_EXPERIMENT';
};
export type MonitoringStatus =
  'NORMAL' | 'ABNORMAL' | 'UNKNOWN' | 'STALE' | 'NOT_APPLICABLE' | 'NO_OBSERVATION';
export type MonitoringWorkspace = {
  asset: AssetRef;
  sourceType: 'CONTROLLED_EXPERIMENT';
  asOf: string;
  status: MonitoringStatus;
  latest: MonitoringEvent<Observation> | null;
  observations: MonitoringEvent<Observation>[];
  maintenance: MonitoringEvent<Maintenance>[];
  maintenanceHistory: MonitoringEvent<Maintenance>[];
  openFaults: number;
  overduePlans: number;
};
export function monitoringEnabled(dev = import.meta.env.DEV, location = window.location) {
  return (
    dev &&
    ['127.0.0.1', 'localhost', '[::1]'].includes(location.hostname) &&
    (selectedBusinessWorkspace()?.unified === true ||
      new URLSearchParams(location.search).get('synMonitoring') === '1')
  );
}
const object = (v: unknown): Record<string, unknown> => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) throw new Error('状态响应格式异常');
  return v as Record<string, unknown>;
};
export function parseWorkspace(value: unknown): MonitoringWorkspace {
  const v = object(value),
    a = object(v.asset);
  if (
    v.sourceType !== 'CONTROLLED_EXPERIMENT' ||
    !['NORMAL', 'ABNORMAL', 'UNKNOWN', 'STALE', 'NOT_APPLICABLE', 'NO_OBSERVATION'].includes(
      String(v.status),
    ) ||
    typeof v.asOf !== 'string' ||
    !Number.isFinite(Date.parse(v.asOf)) ||
    typeof a.assetId !== 'string' ||
    typeof a.digest !== 'string' ||
    !/^[a-f0-9]{64}$/.test(a.digest) ||
    !Number.isSafeInteger(a.publishedVersion) ||
    !Array.isArray(v.observations) ||
    !Array.isArray(v.maintenance) ||
    !Array.isArray(v.maintenanceHistory)
  )
    throw new Error('状态响应契约异常，未采用任何演示替代值');
  for (const event of [
    v.latest,
    ...v.observations,
    ...v.maintenance,
    ...v.maintenanceHistory,
  ].filter(Boolean)) {
    const e = object(event),
      ea = object(e.asset);
    if (
      e.sourceType !== 'CONTROLLED_EXPERIMENT' ||
      ea.assetId !== a.assetId ||
      ea.country !== a.country ||
      ea.agency !== a.agency ||
      !e.data ||
      typeof e.digest !== 'string'
    )
      throw new Error('状态来源或装备范围不匹配');
  }
  for (const event of [v.latest, ...v.observations].filter(Boolean)) {
    const e = object(event),
      d = object(e.data);
    if (
      e.kind !== 'OBSERVATION' ||
      d.sourceType !== 'CONTROLLED_EXPERIMENT' ||
      d.sourceId !== 'SYN-LAB-05' ||
      !['RUNNING', 'STOPPED', 'FAULT', 'UNKNOWN', 'NOT_APPLICABLE'].includes(String(d.operation)) ||
      !['ONLINE', 'OFFLINE', 'UNKNOWN', 'NOT_APPLICABLE'].includes(String(d.connection))
    )
      throw new Error('实验观测状态无效');
    for (const t of ['observedAt', 'receivedAt', 'expiresAt'])
      if (typeof d[t] !== 'string' || !Number.isFinite(Date.parse(d[t] as string)))
        throw new Error('观测时间无效');
    for (const key of ['battery', 'temperature', 'runtimeHours']) {
      const m = object(d[key]);
      if (
        !['MEASURED', 'UNKNOWN', 'NOT_APPLICABLE'].includes(String(m.state)) ||
        (m.state === 'MEASURED'
          ? typeof m.value !== 'number' || !Number.isFinite(m.value)
          : m.value !== null)
      )
        throw new Error('观测数值无效');
    }
  }
  return v as unknown as MonitoringWorkspace;
}
export function createMonitoringAdapter(auth: MonitoringAuth) {
  const root = apiBaseOrigin() + '/management/local-asset/equipment-monitoring';
  async function request(
    suffix: string,
    body?: unknown,
    key?: string,
    signal?: AbortSignal,
  ): Promise<unknown> {
    const r = await workspaceFetch(root + suffix, {
      method: body ? 'POST' : 'GET',
      ...(signal ? { signal } : {}),
      headers: {
        'X-Rescue-Synthetic-Session': auth.session,
        ...(body
          ? {
              'Content-Type': 'application/json',
              'X-Rescue-CSRF': auth.csrf,
              'Idempotency-Key': key!,
            }
          : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const value: unknown = await r.json();
    if (!r.ok) throw new Error('服务未确认 · ' + String(object(value).error ?? r.status));
    return value;
  }
  return {
    async overview(signal?: AbortSignal) {
      const v = object(await request('', undefined, undefined, signal));
      if (v.sourceType !== 'CONTROLLED_EXPERIMENT' || !Array.isArray(v.items))
        throw new Error('实验状态来源异常');
      return v.items.map(parseWorkspace);
    },
    async workspace(id: string, signal?: AbortSignal) {
      const v = parseWorkspace(
        await request('/' + encodeURIComponent(id), undefined, undefined, signal),
      );
      if (v.asset.assetId !== id) throw new Error('装备响应不匹配');
      return v;
    },
    async write(
      asset: AssetRef,
      kind: 'observations' | 'maintenance',
      data: unknown,
      key: string,
      record?: MonitoringEvent<Maintenance>,
    ) {
      return request(
        '/' +
          encodeURIComponent(asset.assetId) +
          '/' +
          kind +
          (record ? '/' + encodeURIComponent(record.recordId) : ''),
        {
          assetVersion: asset.publishedVersion,
          assetDigest: asset.digest,
          data,
          ...(kind === 'maintenance' ? { expectedRevision: record?.revision ?? 0 } : {}),
        },
        key,
      );
    },
  };
}
